
import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { api } from '@/utils/api-client';
import { useToast } from '@/hooks/useToast';

export interface TimezoneSettings {
  defaultTimezone: string;
  displayFormat24h: boolean;
  dateFormat: string;
  firstDayOfWeek: 'sunday' | 'monday';
  showTimezoneSelector: boolean;
  useManualOffset?: boolean;
  manualOffsetHours?: number;
  manualOffsetMinutes?: number;
}

interface TimezoneContextType {
  settings: TimezoneSettings;
  isLoading: boolean;
  error: any;
  updateSettings: (newSettings: Partial<TimezoneSettings>) => Promise<void>;
  formatDate: (date: Date | string) => string;
  formatTime: (date: Date | string) => string;
  formatDateTime: (date: Date | string) => string;
  refetch: () => void;
  detectedTimezone: string;
  isAutoDetected: boolean;
}

export const DEFAULT_TIMEZONE_FALLBACK = 'Asia/Almaty';
export const TIMEZONE_PREFERENCE_SET_KEY = 'timezone_preference_set';

const defaultSettings: TimezoneSettings = {
  defaultTimezone: DEFAULT_TIMEZONE_FALLBACK,
  displayFormat24h: true,
  dateFormat: 'DD.MM.YYYY',
  firstDayOfWeek: 'monday',
  showTimezoneSelector: true,
  useManualOffset: false,
  manualOffsetHours: 0,
  manualOffsetMinutes: 0,
};

const TimezoneContext = createContext<TimezoneContextType | undefined>(undefined);

export const useTimezone = () => {
  const context = useContext(TimezoneContext);
  if (context === undefined) {
    throw new Error('useTimezone must be used within a TimezoneProvider');
  }
  return context;
};

interface TimezoneProviderProps {
  children: ReactNode;
}

/**
 * Detects the user's browser timezone using the Intl API.
 * Falls back to DEFAULT_TIMEZONE_FALLBACK ('Asia/Almaty') if detection fails or runs on server.
 *
 * @returns {string} IANA timezone string (e.g., 'America/New_York')
 */
export const detectBrowserTimezone = (): string => {
  if (typeof window === 'undefined') return DEFAULT_TIMEZONE_FALLBACK;

  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timezone && typeof timezone === 'string') {
      return timezone;
    }
  } catch (error) {
    console.warn('Failed to detect browser timezone:', error);
  }
  return DEFAULT_TIMEZONE_FALLBACK;
};

export const TimezoneProvider: React.FC<TimezoneProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<TimezoneSettings>(defaultSettings);
  const [detectedTimezone, setDetectedTimezone] = useState<string>(DEFAULT_TIMEZONE_FALLBACK);
  const [isAutoDetected, setIsAutoDetected] = useState<boolean>(false);
  
  const toast = useToast();

  // Fetch timezone settings
  const settingsQuery = api.system.timezone.getTimezoneSettings.useQuery();
  const updateSettingsMutation = api.system.timezone.updateTimezoneSettings.useMutation();

  // Detect browser timezone on mount
  useEffect(() => {
    const detected = detectBrowserTimezone();
    setDetectedTimezone(detected);
  }, []);

  // Update local state when query data changes and handle first-visit detection
  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (settingsQuery.data) {
      const hasPreferenceSet = localStorage.getItem(TIMEZONE_PREFERENCE_SET_KEY) === 'true';
      
      // Logic for first-visit detection:
      // 1. If we have server settings and they are NOT the default fallback, 
      //    or if the user has already manually set a preference,
      //    we use the server settings and mark as NOT auto-detected.
      if (hasPreferenceSet || settingsQuery.data.defaultTimezone !== DEFAULT_TIMEZONE_FALLBACK) {
        setSettings(settingsQuery.data);
        setIsAutoDetected(false);
      } else {
        // 2. Otherwise (first visit, or server has default and no manual flag),
        //    we use the detected timezone and mark as auto-detected.
        const detected = detectBrowserTimezone();
        setSettings({
          ...settingsQuery.data,
          defaultTimezone: detected,
        });
        setIsAutoDetected(true);
      }
    }
  }, [settingsQuery.data]);

  const updateSettings = async (newSettings: Partial<TimezoneSettings>) => {
    try {
      const updatedSettings = { ...settings, ...newSettings };
      await updateSettingsMutation.mutateAsync(updatedSettings);
      
      // Update local storage and state
      if (typeof window !== 'undefined') {
        localStorage.setItem(TIMEZONE_PREFERENCE_SET_KEY, 'true');
      }
      
      setSettings(updatedSettings);
      setIsAutoDetected(false);
      await settingsQuery.refetch();
      
      toast({
        title: 'Settings Updated',
        description: 'Timezone settings have been updated successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error updating timezone settings:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update timezone settings',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      throw error;
    }
  };

  // Apply manual offset to a date if enabled
  const applyManualOffset = useCallback((date: Date): Date => {
    if (!settings.useManualOffset || !date || !(date instanceof Date) || isNaN(date.getTime())) {
      return date;
    }
    
    const offsetMs = ((settings.manualOffsetHours || 0) * 60 + (settings.manualOffsetMinutes || 0)) * 60 * 1000;
    return new Date(date.getTime() + offsetMs);
  }, [settings.useManualOffset, settings.manualOffsetHours, settings.manualOffsetMinutes]);

  // Date formatting with manual offset support
  const formatDate = useCallback((date: Date | string, format?: string): string => {
    if (!date) return 'N/A';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (!dateObj || !(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
      return 'Invalid Date';
    }
    const adjustedDate = applyManualOffset(dateObj);
    
    // Use UTC methods to preserve original database time (no browser timezone conversion)
    const year = adjustedDate.getUTCFullYear();
    const month = (adjustedDate.getUTCMonth() + 1).toString().padStart(2, '0');
    const day = adjustedDate.getUTCDate().toString().padStart(2, '0');
    
    // Apply the selected date format
    const selectedFormat = format || settings.dateFormat;
    
    switch (selectedFormat) {
      case 'YYYY-MM-DD':
        return `${year}-${month}-${day}`;
      case 'DD/MM/YYYY':
        return `${day}/${month}/${year}`;
      case 'MM/DD/YYYY':
        return `${month}/${day}/${year}`;
      case 'DD-MM-YYYY':
        return `${day}-${month}-${year}`;
      case 'YYYY/MM/DD':
        return `${year}/${month}/${day}`;
      case 'DD.MM.YYYY':
      default:
        return `${day}.${month}.${year}`;
    }
  }, [applyManualOffset, settings.dateFormat]);

  // Time formatting with manual offset support and 12/24 hour format
  const formatTime = useCallback((date: Date | string): string => {
    if (!date) return 'N/A';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (!dateObj || !(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
      return 'Invalid Time';
    }
    const adjustedDate = applyManualOffset(dateObj);
    
    // Use UTC methods to preserve original database time (no browser timezone conversion)
    let hours = adjustedDate.getUTCHours();
    const minutes = adjustedDate.getUTCMinutes().toString().padStart(2, '0');
    const seconds = adjustedDate.getUTCSeconds().toString().padStart(2, '0');
    
    if (!settings.displayFormat24h) {
      // 12-hour format
      const period = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      return `${hours}:${minutes}:${seconds} ${period}`;
    }
    
    // 24-hour format
    return `${hours.toString().padStart(2, '0')}:${minutes}:${seconds}`;
  }, [applyManualOffset, settings.displayFormat24h]);

  // Format date and time together
  const formatDateTime = useCallback((date: Date | string): string => {
    if (!date) return 'N/A';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (!dateObj || !(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
      return 'Invalid Date';
    }
    const formattedDate = formatDate(date);
    const formattedTime = formatTime(date);
    return `${formattedDate} ${formattedTime}`;
  }, [formatDate, formatTime]);

  const contextValue: TimezoneContextType = {
    settings,
    isLoading: settingsQuery.isLoading,
    error: settingsQuery.error,
    updateSettings,
    formatDate,
    formatTime,
    formatDateTime,
    refetch: settingsQuery.refetch,
    detectedTimezone,
    isAutoDetected,
  };

  return (
    <TimezoneContext.Provider value={contextValue}>
      {children}
    </TimezoneContext.Provider>
  );
}; 
